import React from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Container, 
  Divider, 
  Grid, 
  Card, 
  CardContent,
  Chip,
  alpha,
  Stack
} from '@mui/material';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import HandshakeIcon from '@mui/icons-material/Handshake';
import PublicIcon from '@mui/icons-material/Public';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PeopleIcon from '@mui/icons-material/People';
import SecurityIcon from '@mui/icons-material/Security';
import GavelIcon from '@mui/icons-material/Gavel';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ContactSupportIcon from '@mui/icons-material/ContactSupport';
import SearchIcon from '@mui/icons-material/Search';
import BarChartIcon from '@mui/icons-material/BarChart';
import UpdateIcon from '@mui/icons-material/Update';
import HistoryIcon from '@mui/icons-material/History';
import SettingsIcon from '@mui/icons-material/Settings';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import StorageIcon from '@mui/icons-material/Storage';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import ShieldIcon from '@mui/icons-material/Shield';
import FlashOnIcon from '@mui/icons-material/FlashOn';
import LayersIcon from '@mui/icons-material/Layers';
import SD_logo from '../assests/shipperdock.png';

const COLORS = {
  primary: '#11224d',
  secondary: '#0e1d3a',
  warning: '#ffb300',
};

const Section = ({ title, children, icon: Icon, gradient = false }) => (
  <Box sx={{ mb: 3 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2.5 }}>
      {Icon && (
        <Box
          sx={{
            background: gradient 
              ? `linear-gradient(135deg, ${COLORS.warning} 0%, #f59e0b 100%)`
              : `linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.secondary} 100%)`,
            p: 1.25,
            borderRadius: 2,
            mr: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: `0 4px 12px ${alpha(COLORS.primary, 0.15)}`,
          }}
        >
          <Icon sx={{ color: 'white', fontSize: 24 }} />
        </Box>
      )}
      <Typography
        variant="h5"
        sx={{
          fontWeight: 700,
          color: COLORS.primary,
          fontSize: '1.5rem',
        }}
      >
        {title}
      </Typography>
    </Box>
    <Box sx={{ pl: Icon ? 6 : 0 }}>{children}</Box>
    <Divider
      sx={{
        mt: 4,
        background: `linear-gradient(to right, transparent, ${alpha(COLORS.primary, 0.2)}, transparent)`,
        height: 1,
      }}
    />
  </Box>
);

const ProcessFlow = () => (
  <Box
    sx={{
      my: 10,
      p: { xs: 4, md: 8 },
      background: `linear-gradient(135deg, ${alpha(COLORS.primary, 0.03)} 0%, ${alpha(COLORS.warning, 0.05)} 100%)`,
      borderRadius: 4,
      position: 'relative',
      overflow: 'hidden',
    }}
  >
    <Box sx={{ textAlign: 'center', mb: 2 }}> 
      <Chip
        label="PLATFORM WORKFLOW"
        sx={{
          bgcolor: COLORS.warning,
          color: COLORS.primary,
          fontWeight: 'bold',
          fontSize: '0.975rem',
          px: 3,
          py: 2.5,
          mt: -6,
          mb: 2,
          boxShadow: `0 8px 24px ${alpha(COLORS.warning, 0.4)}`,
        }}
      />
      <Typography
        variant="h4"
        sx={{
          fontWeight: 'bold',
          color: COLORS.primary,
          mb: 2,
        }}
      >
        How ShipperDock Connects the Freight Ecosystem
      </Typography>
      <Typography
        variant="h6"
        sx={{
          color: 'text.secondary',
          maxWidth: '800px',
        }}
      >
        A neutral technology platform enabling seamless coordination between all market participants
      </Typography>
    </Box>

    <Box sx={{ py: 3, width: '100%', display: 'flex', justifyContent: 'flex-start' }}>
      <Stack 
        direction="row" 
        spacing={2} 
        
        alignItems="center" 
        justifyContent="flex-start" 
        sx={{ 
          width: '100%',
          maxWidth: 'lg',
          ml: 0, 
          mr: 'auto' 
        }}
      >
    
        <Card
          elevation={8}
          sx={{
            flex: 1,
            borderTop: `4px solid ${COLORS.primary}`,
            borderRadius: 5,
            transition: 'all 0.3s ease',
            '&:hover': { transform: 'translateY(-8px)' },
          }}
        >
          <CardContent sx={{ textAlign: 'center', py: 3 }}>
            <Box sx={{ width: 60, height: 60, borderRadius: 8, background: alpha(COLORS.primary, 0.1), display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2 }}>
              <WarehouseIcon sx={{ fontSize: 32, color: COLORS.primary }} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: COLORS.primary, mb: 1 }}>Shippers</Typography>
            <Typography variant="caption" display="block" sx={{ color: 'text.secondary', mb: 2, minHeight: '40px' }}>
              Post freight loads with detailed requirements
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Chip label="Manufacturers" size="small" sx={{ fontSize: '0.65rem', bgcolor: alpha(COLORS.primary, 0.1) }} />
              <Chip label="Distributors" size="small" sx={{ fontSize: '0.65rem', bgcolor: alpha(COLORS.primary, 0.1) }} />
            </Box>
          </CardContent>
        </Card>

        <ArrowForwardIcon sx={{ color: COLORS.warning, flexShrink: 0 }} />

     
        <Card
          elevation={12}
          sx={{
            flex: 1.1,
            background: `linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.secondary} 100%)`,
            borderTop: `4px solid ${COLORS.warning}`,
            color: 'white',
            borderRadius: 5,
            transition: 'all 0.3s ease',
            '&:hover': { transform: 'translateY(-8px)' },
          }}
        >
          <CardContent sx={{ textAlign: 'center', py: 3 }}>
            <Box sx={{ width: 60, height: 60, borderRadius: 8, bgcolor: alpha('#fff', 0.15), display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2 }}>
              <PublicIcon sx={{ fontSize: 32, color: 'white' }} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>ShipperDock Platform</Typography>
            <Typography variant="caption" display="block" sx={{ color: alpha('#fff', 0.9), mb: 2, minHeight: '40px' }}>
              Real-time matching, visibility & coordination hub
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
              <Chip label="Live Data" size="small" sx={{ fontSize: '0.65rem', bgcolor: alpha('#fff', 0.2), color: 'white' }} />
              <Chip label="Secure" size="small" sx={{ fontSize: '0.65rem', bgcolor: alpha('#fff', 0.2), color: 'white' }} />
            </Box>
          </CardContent>
        </Card>

        <ArrowForwardIcon sx={{ color: COLORS.warning, flexShrink: 0 }} />

        <Card
          elevation={8}
          sx={{
            flex: 1,
            borderTop: `4px solid ${COLORS.primary}`,
            borderRadius: 5,
            transition: 'all 0.3s ease',
            '&:hover': { transform: 'translateY(-8px)' },
          }}
        >
          <CardContent sx={{ textAlign: 'center', py: 3 }}>
            <Box sx={{ width: 60, height: 60, borderRadius: 8, background: alpha('#10b981', 0.1), display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2 }}>
              <LocalShippingIcon sx={{ fontSize: 32, color: COLORS.primary }} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: COLORS.primary, mb: 1 }}>Carriers</Typography>
            <Typography variant="caption" display="block" sx={{ color: 'text.secondary', mb: 2, minHeight: '40px' }}>
              Find optimal loads & post available capacity
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Chip label="Owner-Operators" size="small" sx={{ fontSize: '0.65rem', bgcolor: alpha(COLORS.primary, 0.1) }} />
              <Chip label="Fleets" size="small" sx={{ fontSize: '0.65rem', bgcolor: alpha(COLORS.primary, 0.1) }} />
            </Box>
          </CardContent>
        </Card>
      </Stack>
    </Box>

   
    <Card
      elevation={4}
      sx={{
        mt:2,
        maxWidth: '900px',
        ml: 0, 
        mr: 'auto',
        borderLeft: `4px solid ${COLORS.warning}`,
      }}
    >
      <CardContent sx={{ p: 4 }}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box sx={{ bgcolor: COLORS.warning, p: 2, borderRadius: 2, mr: 2 }}>
                <HandshakeIcon sx={{ fontSize: 32, color: COLORS.primary }} />
              </Box>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: COLORS.primary }}>
                  Flexible Broker Integration
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Participate at any workflow stage
                </Typography>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'left' }}>
              Brokers can facilitate connections and manage operations throughout the entire coordination process
            </Typography>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  </Box>
);
const PlatformCapabilities = () => {
  const capabilities = [
    { icon: SearchIcon, title: "Discovery", desc: "Real-time discovery of freight loads and truck capacity." },
    { icon: GpsFixedIcon, title: "Location", desc: "Precise search by origin, destination, and route." },
    { icon: BarChartIcon, title: "Dashboards", desc: "Visual distribution of market data and trends." },
    { icon: PeopleIcon, title: "Communication", desc: "Access verified party-to-party contact info." },
    { icon: UpdateIcon, title: "Live Updates", desc: "Real-time updates as market activity changes." },
    { icon: HistoryIcon, title: "Tracking", desc: "Tracking of posted loads and interaction history." },
    { icon: SettingsIcon, title: "Management", desc: "Centralized tools for profile preferences." },
    { icon: VerifiedUserIcon, title: "Verification", desc: "Transparency through DOT and MC numbers." },
    { icon: StorageIcon, title: "Structured Data", desc: "Organized data for faster decision-making." }
  ];

  return (
    <Box sx={{ width: '100%', py: 8 }}>
      
      <Container maxWidth="lg"> 
        <Box sx={{ textAlign: 'center', mb: 4, mt:-16 }}>
          <Typography 
            variant="h3" 
            sx={{ fontWeight: 'bold', color: COLORS.primary, mb: 2 }}
          >
            Platform Capabilities
          </Typography>
        </Box>

       
        <Grid container spacing={2} justifyContent="center">
          {capabilities.map((item, index) => (
            <Grid item xs={12} sm={4} md={2} key={index} sx={{ display: 'flex', justifyContent: 'center' }}>
              <Card
                elevation={0}
                sx={{
                  
                  width: '100%',
                  maxWidth: 260, 
                  aspectRatio: '1/1',
                  
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  borderRadius: 5,
                  border: `1px solid ${alpha(COLORS.primary, 0.08)}`,
                  bgcolor: 'white',
                  transition: 'all 0.4s ease',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: `0 15px 30px ${alpha(COLORS.primary, 0.1)}`,
                    borderColor: COLORS.warning,
                    '& .icon-box': {
                      bgcolor: COLORS.warning,
                      color: COLORS.primary,
                      transform: 'scale(1.05) rotate(5deg)',
                    },
                  },
                }}
              >
                <CardContent sx={{ p: 2, textAlign: 'center' }}>
                  <Box
                    className="icon-box"
                    sx={{
                      width: 50,
                      height: 50,
                      borderRadius: 3,
                      bgcolor: alpha(COLORS.primary, 0.05),
                      color: COLORS.primary,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mx: 'auto',
                      mb: 2,
                      transition: 'all 0.3s ease',
                    }}
                  >
                    <item.icon sx={{ fontSize: 24 }} />
                  </Box>
                  <Typography 
                    variant="subtitle1" 
                    sx={{ fontWeight: 'bold', color: COLORS.primary, mb: 1, fontSize: '0.95rem' }}
                  >
                    {item.title}
                  </Typography>
                  <Typography 
                    variant="caption" 
                    sx={{ color: 'text.secondary', lineHeight: 1.4, display: 'block', px: 1 }}
                  >
                    {item.desc}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};
const StatsBar = () => {
  const stats = [
    { icon: GpsFixedIcon, label: "Market Focus", value: "U.S. Market", color: '#3b82f6' },
    { icon: ShieldIcon, label: "Platform Type", value: "Neutral Tech", color: '#8b5cf6' },
    { icon: FlashOnIcon, label: "Updates", value: "Real-time", color: '#10b981' },
    { icon: StorageIcon, label: "Data Integrity", value: "User-Driven", color: '#f59e0b' }
  ];

  return (
    <Container maxWidth="xl" sx={{ px: { xs: 1, md: 0 } }}> {/* Reduced outer side padding */}
      <Grid 
        container 
        spacing={2} // Reduced spacing from 4 to 2 to pull cards closer
        justifyContent="center" 
        sx={{ 
          mb: -8, 
          mt: -2,
          width: '100%',
          mx: 'auto'
        }}
      >
        {stats.map((stat, index) => (
          <Grid item xs={12} sm={6} md={3} key={index} sx={{ display: 'flex', justifyContent: 'center' }}>
            <Card
              elevation={4}
              sx={{
                textAlign: 'center',
                p: { xs: 2, md: 3 }, // Reduced internal padding to save horizontal space
                width: '100%',
                maxWidth: '300px', 
                minHeight: '180px', 
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                transition: 'all 0.3s ease',
                borderRadius: 5,
                border: `1px solid ${alpha(stat.color, 0.1)}`,
                '&:hover': {
                  transform: 'translateY(-10px)',
                  boxShadow: `0 20px 40px ${alpha(stat.color, 0.15)}`,
                },
              }}
            >
              <Box
                sx={{
                  width: 60,
                  height: 60,
                  borderRadius: 3,
                  bgcolor: alpha(stat.color, 0.1),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 2,
                }}
              >
                <stat.icon sx={{ fontSize: 32, color: stat.color }} />
              </Box>
              
              <Typography 
                variant="caption" 
                sx={{ 
                  color: 'text.secondary', 
                  fontWeight: 700, 
                  textTransform: 'uppercase', 
                  letterSpacing: 0.5, // Reduced letter spacing to keep label in one line
                  mb: 0.5,
                  fontSize: '0.7rem',
                  whiteSpace: 'nowrap'
                }}
              >
                {stat.label}
              </Typography>
              
              <Typography 
                variant="h6"
                sx={{ 
                  fontWeight: 800, 
                  color: COLORS.primary, 
                  // Dynamic font size: smaller on laptops to prevent line breaks
                  fontSize: { xs: '1.1rem', lg: '1.25rem' }, 
                  whiteSpace: 'nowrap', // Forces the value to stay on one line
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}
              >
                {stat.value}
              </Typography>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Container>
  );
};
const CompanyProfile = () => {
  return (
    <Box sx={{ bgcolor: '#f8f9fc', minHeight: '100vh' }}>
    
      <Box
        sx={{
          position: 'relative',
          overflow: 'hidden',
          
          background: `linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.secondary} 50%, ${COLORS.primary} 100%)`,
          color: 'white',
        }}
      >
      
        <Box
          sx={{
            position: 'relative',
            bgcolor: '#ffffff', 
            color: COLORS.primary, 
          }}
        >
          <Container maxWidth="lg" sx={{ position: 'relative', py: { xs: 1, md: 2 } }}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            
           
            <Card
              elevation={12}
              sx={{
                maxWidth: '1100px',
                mx: 'auto',
                borderRadius: 6,
                background: `linear-gradient(145deg, ${alpha(COLORS.primary, 0.04)} 0%, ${alpha(COLORS.warning, 0.08)} 100%)`,
                border: `2px solid ${alpha(COLORS.warning, 0.3)}`,
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '6px',
                  background: `linear-gradient(90deg, ${COLORS.primary} 0%, ${COLORS.warning} 50%, ${COLORS.primary} 100%)`,
                },
              }}
            >
              <Box sx={{ mb: 5, mt: 3,mb: 1 }}>
              <img 
                src={SD_logo} 
                alt="ShipperDock Logo" 
                style={{ 
                  maxWidth: '400px', 
                  height: 'auto',
                  display: 'block',
                  margin: '0 auto'
                  
                }} 
              />
            </Box>
              <CardContent sx={{ p: { xs: 6, md: 4 }, pt: { xs: 5, md: 2 } }}>
                

                <Typography 
                  variant="h6" 
                  sx={{ 
                    mb: 3, 
                    lineHeight: 1.8,
                    color: COLORS.primary,
                    fontWeight: 500,
                    textAlign: 'center',
                    px: { xs: 0, md: 2 },
                  }}
                >
                  ShipperDock is a web-based freight loadboard platform designed to support modern logistics operations through continuously updated market visibility, structured data, and flexible collaboration tools. Built for shippers, carriers, and brokers, the platform provides a centralized digital environment where freight opportunities and available equipment can be discovered, reviewed, and managed efficiently.
                </Typography>
                
                <Divider sx={{ my: 3, borderColor: alpha(COLORS.warning, 0.3) }} />
                
                <Typography 
                  variant="body1" 
                  sx={{ 
                    lineHeight: 1.8,
                    color: 'text.secondary',
                    fontSize: '1.05rem',
                    fontWeight: 300,
                    textAlign: 'center',
                    px: { xs: 0, md: 2 },
                  }}
                >
                  ShipperDock operates as a neutral technology platform focused on improving visibility and communication across the freight ecosystem while allowing market participants to choose how they conduct business. The platform is developed as a product of Goodlanes Transportation Services LLC.
                </Typography>

               
              </CardContent>
            </Card>
          </Box>
        </Container>

     
          
        </Box>
      </Box>

      <Container maxWidth="lg" sx={{ py: 6 }}>
     
        <StatsBar />

        <ProcessFlow />

     
        <PlatformCapabilities />

   
        <Paper
          elevation={6}
          sx={{
            p: { xs: 4, md: 8 },
            borderRadius: 4,
            mt: -4,
          }}
        >
          <Section title="Industry Context" icon={PublicIcon}>
            <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.8, color: 'text.secondary' }}>
              The freight transportation industry depends on timely access to information, accurate market signals, and efficient coordination among multiple parties. As shipment volumes increase and delivery timelines become more compressed, logistics professionals require platforms that provide continuously refreshed market information while supporting a variety of operating models.
            </Typography>
            <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.8, color: 'text.secondary' }}>
              Modern loadboards address this need by aggregating market activity and updating listings as users post, modify, and remove loads and equipment. ShipperDock is designed to operate within this framework by providing a reliable, technology-driven view of freight market activity based on current and recently updated information.
            </Typography>
          </Section>

          <Section title="Platform Purpose" icon={VisibilityIcon} gradient>
            <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.8, color: 'text.secondary' }}>
              The primary purpose of ShipperDock is to support freight discovery and coordination through technology. The platform enables users to post loads and available trucks, search by origin and destination, and evaluate opportunities as market activity changes.
            </Typography>
            <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.8, color: 'text.secondary' }}>
              ShipperDock is intended to enhance operational visibility and efficiency without participating in freight transactions or contractual agreements. The platform does not replace professional judgment, negotiation, or regulatory responsibility and does not interfere with how users choose to structure their business relationships.
            </Typography>
          </Section>

          <Section title="Direct Connectivity and Flexible Engagement" icon={HandshakeIcon} gradient>
            <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.8, color: 'text.secondary' }}>
              ShipperDock enables shippers and carriers to connect directly through the platform by providing visibility to relevant load and equipment information along with user-provided contact details. This direct connectivity supports efficient communication, faster clarification of requirements, and timely coordination when parties choose to engage directly.
            </Typography>
            <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.8, color: 'text.secondary' }}>
              At the same time, the platform fully supports broker participation and broker-assisted workflows. ShipperDock does not limit or prescribe how users engage with one another and does not favor any specific operating model. Shippers, carriers, and brokers may interact in the manner that best aligns with their operational needs, business relationships, and internal processes.
            </Typography>
          </Section>

          <Section title="Neutral Technology Role" icon={SecurityIcon}>
            <Card
              sx={{
                bgcolor: alpha(COLORS.primary, 0.03),
                borderLeft: `4px solid ${COLORS.primary}`,
                boxShadow: 'none',
              }}
            >
              <CardContent sx={{ p: 4 }}>
                <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.8, color: 'text.secondary' }}>
                  ShipperDock functions strictly as a software platform. It does not act as a broker, motor carrier, shipper, dispatcher, or payment intermediary. The platform does not arrange transportation, negotiate freight terms, verify creditworthiness, or guarantee payment, performance, or delivery.
                </Typography>
                <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.8, color: 'text.secondary' }}>
                  All negotiations, agreements, and transactions occur directly between users. ShipperDock's role is limited to providing access to technology tools that support market visibility, discovery, and communication.
                </Typography>
              </CardContent>
            </Card>
          </Section>

          <Section >
 
  <Box sx={{ textAlign: 'center', mb: 6 }}>
    <Typography 
      variant="h4" 
      sx={{ 
        fontWeight: 'bold', 
        color: COLORS.primary, 
        mb: 2 
      }}
    >
        
      Target Users
    </Typography>
    <Typography 
      variant="body1" 
      sx={{ 
        fontSize: '1.1rem', 
        color: 'text.secondary', 
        maxWidth: '700px', 
        mx: 'auto' 
      }}
    >
      ShipperDock is designed to serve professionals across the freight and logistics ecosystem:
    </Typography>
  </Box>


  <Grid container spacing={2} justifyContent="center" sx={{ mb: 6 }}>
    {[
      { title: "Shippers", desc: "Manufacturers, distributors, and exporters", icon: WarehouseIcon, color: '#3b82f6' },
      { title: "Carriers", desc: "Owner-operators and  mid-sized fleets", icon: LocalShippingIcon, color: '#10b981' },
      { title: "Brokers", desc: "Seeking efficient load posting and visibility", icon: HandshakeIcon, color: '#8b5cf6' }
    ].map((user, index) => (
      <Grid item xs={12} sm={4} md={4} key={index} sx={{ display: 'flex', justifyContent: 'center' }}>
        <Card
          elevation={0}
          sx={{
            width: '100%',
            
            maxWidth: 270, 
            aspectRatio: '1/1',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            borderRadius: 6,
            border: `1px solid ${alpha(COLORS.primary, 0.08)}`,
            bgcolor: 'white',
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-8px)',
              boxShadow: `0 15px 30px ${alpha(user.color, 0.1)}`,
              borderColor: user.color,
            },
          }}
        >
          <CardContent sx={{ p: 2, textAlign: 'center' }}>
            <Box
              sx={{
                width: 58,
                height: 58,
                borderRadius: 3,
                background: `linear-gradient(135deg, ${user.color} 0%, ${alpha(user.color, 0.8)} 100%)`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 2,
              }}
            >
              <user.icon sx={{ fontSize: 30, color: 'white' }} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: COLORS.primary, mb: 1, fontSize: '1.1rem' }}>
              {user.title}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.4, display: 'block', px: 1 }}>
              {user.desc}
            </Typography>
          </CardContent>
        </Card>
      </Grid>
    ))}
  </Grid>
</Section>
<Section title="" >
   
  <Box sx={{ textAlign: 'center', mb: 6 }}>
     <Typography 
      variant="h4" 
      sx={{ 
        fontWeight: 'bold', 
        color: COLORS.primary, 
        mb: 2 
      }}
    >
        
      Design Philosophy
    </Typography>
    <Typography 
      variant="body1" 
      sx={{ 
        fontSize: '1.1rem', 
        lineHeight: 1.8, 
        color: 'text.secondary', 
        maxWidth: '800px', 
        mx: 'auto' 
      }}
    >
      ShipperDock is built around principles that reflect established loadboard standards:
    </Typography>
  </Box>

  <Grid container spacing={4} justifyContent="center">
    {[
      { 
        title: "Market Visibility", 
        icon: VisibilityIcon, 
        desc: "Access to freight activity refreshed as users update listings.",
        bg: `linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.secondary} 100%)`
      },
      { 
        title: "Operational Efficiency", 
        icon: FlashOnIcon, 
        desc: "Reducing manual coordination through centralized digital tools.",
        bg: `linear-gradient(135deg, ${COLORS.warning} 0%, #f59e0b 100%)`
      },
      { 
        title: "Flexibility", 
        icon: LayersIcon, 
        desc: "Supporting direct and broker-assisted workflows equally.",
        bg: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)'
      },
      { 
        title: "Reliability", 
        icon: ShieldIcon, 
        desc: "Scalable technologies designed for consistent performance.",
        bg: 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
      }
    ].map((box, index) => (
      <Grid item xs={12} sm={6} md={6} key={index} sx={{ display: 'flex', justifyContent: 'center' }}>
        <Card
          elevation={6}
          sx={{
        
            width: '100%',
            maxWidth: 340,      
            aspectRatio: '1/1', 
            
            background: box.bg,
            color: 'white',
            borderRadius: 6,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            transition: 'all 0.4s ease',
            '&:hover': {
              transform: 'translateY(-10px)',
              boxShadow: '0 25px 50px rgba(0,0,0,0.25)',
            },
          }}
        >
          <CardContent sx={{ p: 3, textAlign: 'center' }}>
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: 3,
                bgcolor: 'rgba(255, 255, 255, 0.25)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 3,
              }}
            >
              <box.icon sx={{ fontSize: 36, color: 'white' }} />
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2 }}>
              {box.title}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9, lineHeight: 1.6, px: 2 }}>
              {box.desc}
            </Typography>
          </CardContent>
        </Card>
      </Grid>
    ))}
  </Grid>
</Section>

          <Section title="Technology and Data Integrity" icon={SecurityIcon} gradient>
            <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.8, color: 'text.secondary' }}>
              ShipperDock is developed using modern web technologies designed for scalability, performance, and secure access. Role-based permissions, structured data management, and secure authentication help maintain platform integrity while supporting efficient user interaction.
            </Typography>
            <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.8, color: 'text.secondary' }}>
              Market data displayed on the platform reflects information provided and updated by users. While the platform provides timely visibility into market activity, accuracy and availability depend on user participation and updates.
            </Typography>
          </Section>

          <Section title="Compliance and User Responsibility" icon={GavelIcon}>
            <Card
              sx={{
                bgcolor: '#fef2f2',
                borderLeft: '4px solid #ef4444',
                boxShadow: 'none',
              }}
            >
              <CardContent sx={{ p: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                  <GavelIcon sx={{ color: '#ef4444', fontSize: 32, mr: 2, flexShrink: 0 }} />
                  <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.8, color: 'text.secondary' }}>
                    Users are responsible for maintaining all required licenses, permits, insurance coverage, and regulatory compliance applicable to their operations. ShipperDock provides technology tools to support logistics activity but does not replace due diligence, compliance obligations, or professional responsibility.
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Section>

          <Section title="Long-Term Direction" icon={TrendingUpIcon} gradient>
            <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.8, color: 'text.secondary' }}>
              ShipperDock is designed to evolve alongside the freight industry by expanding platform capabilities and enhancing data visibility. Future development may include advanced analytics, intelligent matching tools, expanded visualization features, and mobile access to support on-the-go operations.
            </Typography>
            <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.8, color: 'text.secondary' }}>
              The long-term objective is to provide a reliable, scalable, and neutral digital platform that reflects how modern freight markets operate while supporting efficiency and transparency.
            </Typography>
          </Section>

        <Section >
            <Box sx={{ textAlign: 'center', mb: 6 }}>
    <Typography 
      variant="h4" 
      sx={{ 
        fontWeight: 'bold', 
        color: COLORS.primary, 
        mb: 2,
        mt: -2
        
      }}
    >
        
      Contact and Support
    </Typography>
    
  </Box>
  <Card
    elevation={0}
    sx={{
      position: 'relative',
      overflow: 'hidden',
      borderRadius: 6,
      background: `linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.secondary} 100%)`,
      color: 'white',
      width: '100%',
      maxWidth: '1200px',
      mx: 'auto',
      mt:-2
    }}
  >
  
    <Box sx={{ position: 'absolute', top: -20, right: -20, width: 120, height: 120, borderRadius: '50%', background: 'rgba(255, 255, 255, 0.05)' }} />
    <Box sx={{ position: 'absolute', bottom: -30, left: -30, width: 100, height: 100, borderRadius: '50%', background: alpha(COLORS.warning, 0.1) }} />

    <CardContent sx={{ 
      py: 3, 
      px: { xs: 3, md: 6 },
      position: 'relative', 
      zIndex: 1,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center'
    }}>
      
      <Box
        sx={{
          display: 'inline-flex',
          bgcolor: alpha('#fff', 0.15),
          p: 1,
          borderRadius: 2,
          mb: 1.5,
        }}
      >
        <ContactSupportIcon sx={{ fontSize: 32, color: COLORS.warning }} />
      </Box>

      <Typography variant="h4" sx={{ fontWeight: 800, mb: 0.5, letterSpacing: '-0.5px' }}>
        Get in Touch
      </Typography>
      
      <Typography variant="body2" sx={{ opacity: 0.85, mb: 3, maxWidth: '600px' }}>
        Have questions? Our support team is here to help you optimize your operations.
      </Typography>

      <Box
        component="a"
        href="mailto:support@shipperdock.com"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'white',
          px: 5,
          py: 1.5,
          borderRadius: 50, 
          color: COLORS.primary,
          textDecoration: 'none',
          fontWeight: 'bold',
          fontSize: '1.1rem',
          boxShadow: '0 4px 15px rgba(0,0,0,0.2)',
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 8px 20px rgba(0,0,0,0.3)',
            color: COLORS.secondary
          },
        }}
      >
        support@shipperdock.com
        <ArrowForwardIcon sx={{ ml: 1.5, fontSize: 20 }} />
      </Box>

      <Typography variant="caption" sx={{ mt: 2, opacity: 0.7, fontSize: '0.75rem' }}>
        We typically respond within 24 business hours.
      </Typography>
    </CardContent>
  </Card>
</Section>
        </Paper>
      </Container>

   
      <Box
        sx={{
          height: 12,
          background: `linear-gradient(to right, ${COLORS.primary}, ${COLORS.warning}, ${COLORS.secondary}, ${COLORS.primary})`,
        }}
      />
    </Box>
  );
};

export default CompanyProfile;